import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { StepHeader } from "@freightsimple/ui-core/PageLayout/StepHeader";
import type { CSSProperties, ReactNode } from "react";

/**
 * How wide a section's panel is allowed to get. Three sizes, because the form
 * asks three sizes of question: a single control ("narrow"), the pickup
 * calendar ("medium"), and a two-column form ("wide").
 *
 * Sizing every panel the same makes the small ones look empty and the whole
 * page look padded — a panel should be as wide as its question, and no wider.
 * Written out in full: Tailwind can't see a computed class name.
 */
type SectionWidth = "narrow" | "medium" | "wide" | "wider";

const widthClasses: Record<SectionWidth, string> = {
  narrow: "max-w-[600px]",
  // The pickup calendar, and sized to it: the two months are 608px wide, the
  // panel adds a 32px gutter each side, and the rest is slack so the calendar
  // sits centred with a comfortable margin rather than rattling around in a
  // box far wider than it needs. Don't shrink this much further — under about
  // 686px the picker measures too little room for two months and falls back to
  // one.
  medium: "max-w-[700px]",
  wide: "max-w-[824px]",
  // Shipment Contents alone: its two columns each hold a full row of fields
  // (the NMFC field's placeholder, the L x W x H x weight row), so it needs
  // more room than the others. It was the widest section in Cargo too.
  wider: "max-w-[920px]",
};

interface BookShipmentSectionProps {
  header: ReactNode;
  description?: ReactNode | undefined;
  children?: ReactNode | undefined;
  index: number;
  id?: string | undefined;
  minHeight?: string | undefined;
  /** Defaults to `wide` — most sections are a two-column form. */
  width?: SectionWidth | undefined;
}

function BookShipmentSection(props: BookShipmentSectionProps) {
  // minHeight is per-section (the location sections reserve space so the page
  // doesn't jump while their content loads), so it stays an inline style.
  const style: CSSProperties = { marginBottom: "64px", marginTop: "64px" };
  if (props.minHeight) {
    style.minHeight = props.minHeight;
  }

  return (
    // A max-width, not a width, so the panel gives ground on a narrow screen
    // rather than pushing the page into a horizontal scroll.
    <div
      style={style}
      id={props.id}
      className={`mx-auto w-full ${widthClasses[props.width ?? "wide"]}`}
    >
      {/* stretch, not center: the panel fills the section. StepHeader centres
          its own content, so it doesn't need the Stack to do it. */}
      <Stack gap={6} align="stretch">
        <StepHeader
          step={props.index}
          title={props.header}
          subtitle={props.description}
        />
        {props.children}
      </Stack>
    </div>
  );
}
export default BookShipmentSection;
